// ============================================================================
// MAIN APP COMPONENT - Sustainability Assessment Platform
// Created by Salvador Janeiro
// ============================================================================

import React, { useState } from 'react';
import WelcomePage from './WelcomePage';
import QuestionPage from './QuestionPage';
import IndividualResults from './IndividualResults';
import CompanyResults from './CompanyResults';
import { INDIVIDUAL_QUESTIONS, COMPANY_QUESTIONS } from './data-constants';
import { calculateIndividualResults, calculateCompanyResults } from './calculations';

function App() {
  // --------------------------------------------------------------------------
  // STATE MANAGEMENT
  // --------------------------------------------------------------------------
  
  const [stage, setStage] = useState('welcome'); 
  // Stages: 'welcome', 'questions', 'results'
  
  const [assessmentType, setAssessmentType] = useState(null); 
  // Types: 'individual' or 'company'
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  
  const [answers, setAnswers] = useState({});
  
  const [results, setResults] = useState(null);

  // --------------------------------------------------------------------------
  // DERIVED VALUES
  // --------------------------------------------------------------------------
  
  const questions = assessmentType === 'individual' 
    ? INDIVIDUAL_QUESTIONS 
    : COMPANY_QUESTIONS;

  // --------------------------------------------------------------------------
  // HANDLERS
  // --------------------------------------------------------------------------
  
  const handleSelectType = (type) => {
    setAssessmentType(type);
    setStage('questions');
    setCurrentQuestionIndex(0);
    setAnswers({});
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const handleSubmit = () => {
    let calculatedResults;
    
    if (assessmentType === 'individual') {
      calculatedResults = calculateIndividualResults(answers);
    } else {
      calculatedResults = calculateCompanyResults(answers);
    }
    
    setResults(calculatedResults);
    setStage('results');
  };

  const handleReset = () => {
    setStage('welcome');
    setAssessmentType(null);
    setCurrentQuestionIndex(0);
    setAnswers({});
    setResults(null);
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  
  return (
    <div className="App">
      {/* WELCOME STAGE */}
      {stage === 'welcome' && (
        <WelcomePage onSelectType={handleSelectType} />
      )}

      {/* QUESTIONS STAGE */}
      {stage === 'questions' && (
        <QuestionPage
          questions={questions}
          currentIndex={currentQuestionIndex}
          answers={answers}
          onAnswer={handleAnswer}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onSubmit={handleSubmit}
          assessmentType={assessmentType}
        />
      )}

      {/* RESULTS STAGE */}
      {stage === 'results' && assessmentType === 'individual' && (
        <IndividualResults results={results} onReset={handleReset} />
      )}

      {stage === 'results' && assessmentType === 'company' && (
        <CompanyResults results={results} onReset={handleReset} />
      )}
    </div>
  );
}

export default App;
